import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import SEOHead from "@/components/feature/SEOHead";
import { articles, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 font-sans">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-label cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    url: `https://www.51meiridasai.com/article/${article.slug}`,
    image: article.coverImageUrl,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "51每日大赛",
      url: "https://www.51meiridasai.com/",
    },
    inLanguage: "zh-CN",
    keywords: article.tags.join(","),
    articleSection: category?.name || "",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.51meiridasai.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category?.name || "分类",
        item: `https://www.51meiridasai.com/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://www.51meiridasai.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImageUrl}
        schema={[articleSchema, breadcrumbSchema]}
        articleInfo={{
          publishDate: article.publishDate,
          modifiedDate: article.publishDate,
          author: article.author,
          tags: article.tags,
        }}
      />

      <div className="min-h-screen bg-background-50 font-sans">
        <Navbar />

        {/* Article Header */}
        <section className="pt-28 pb-6 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category?.name || "分类", href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.isExclusive && (
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-label rounded-full">
                  [独家爆料]
                </span>
              )}
              {article.isHot && (
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-label rounded-full flex items-center gap-1">
                  <i className="ri-fire-line text-[10px]"></i>热门
                </span>
              )}
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-label rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount > 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万 阅读`
                  : `${article.readCount} 阅读`}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.commentCount} 评论
              </span>
            </div>
          </div>
        </section>

        {/* Article Cover */}
        <section className="pb-8">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="relative rounded-2xl overflow-hidden aspect-[16/9]">
              <img
                src={article.coverImageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-6 pb-14">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <article className="prose prose-lg max-w-none">
              <div className="bg-background-100 rounded-xl p-6 mb-8 border-l-4 border-primary-500">
                <p className="text-lg text-foreground-800 leading-relaxed font-heading">
                  &ldquo;{article.excerpt}&rdquo;
                </p>
              </div>

              <div className="space-y-6 text-foreground-800 leading-relaxed text-base">
                <p>
                  据知情人士透露，这一事件早在数月前就已经有了苗头。多位圈内人士向51每日大赛爆料称，
                  相关方一直在试图控制舆论走向，但随着越来越多的证据浮出水面，真相已经无法掩盖。
                </p>
                <p>
                  我们通过多方渠道交叉验证，确认了这一爆料的核心信息。在互联网时代，
                  <strong>每日大赛吃瓜爆料</strong>已经成为公众了解事件真相的重要途径。
                  51每日大赛作为专业的<strong>每日大赛黑料网</strong>，一直致力于为吃瓜群众提供最真实、最及时的爆料内容。
                </p>
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                  事件背景
                </h3>
                <p>
                  事情的起因可以追溯到更早。根据我们的独家消息来源，相关当事人在行业内已经多次因为类似问题引发争议。
                  多位业内人士在接受51每日大赛采访时表示，这种情况并不罕见，但这次事件的规模和影响范围远超以往。
                </p>
                <p>
                  从{article.publishDate}开始，相关话题在各大社交平台持续发酵。
                  <strong>51每日大赛在线观看</strong>入口的访问量在短时间内暴涨，
                  大量吃瓜群众涌入平台追踪最新进展。这也是<strong>51每日大赛今日</strong>最受关注的热门话题。
                </p>
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                  深度分析
                </h3>
                <p>
                  业内人士分析认为，这一事件折射出了当前<strong>网红塌房</strong>频发的深层原因。
                  在人设经济的驱动下，部分网红为了维持流量和商业价值，不惜采取造假手段。
                  而一旦真相败露，往往引发连锁反应，导致整个商业合作链条崩塌。
                </p>
                <p>
                  我们在调查过程中发现，类似事件并非孤例。根据平台数据统计，仅过去一年就有超过50起
                  规模较大的网红塌房事件被曝光，其中不乏拥有千万级粉丝的头部网红。
                </p>
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                  后续跟踪
                </h3>
                <p>
                  51每日大赛将持续跟进此事件的最新进展。根据目前掌握的信息，涉事多方正在积极应对，
                  但具体处理结果仍有待观察。我们将在获取最新消息后第一时间更新。
                </p>
                <p>
                  对于广大吃瓜群众来说，<strong>吃瓜爆料入口</strong>始终保持开放。
                  如果您掌握更多内幕信息，欢迎通过投稿渠道向我们提供线索，
                  51每日大赛承诺将严格保护爆料人隐私。
                </p>
              </div>

              {/* Tags bottom */}
              <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs font-label rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mt-6">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer">
                  <i className="ri-link text-foreground-600"></i>
                </button>
              </div>
            </article>

            {/* Prev / Next navigation */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 text-foreground-600 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回上一页
              </button>
              <Link
                to={category ? `/category/${article.category}` : "/"}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer"
              >
                更多{category?.name || "文章"}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20atmospheric%20gradient%20with%20deep%20orange%20and%20dark%20red%20tones%2C%20soft%20flowing%20shapes%2C%20editorial%20cinematic%20style%2C%20dramatic%20center%20lighting%2C%20dark%20edges%2C%20moody%20and%20mysterious%20aesthetic%2C%20no%20text%20or%20people%2C%20elegant%20minimal%20composition%2C%20organic%20abstract%20forms&width=1920&height=600&seq=article-cta-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
          </div>
          <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              关注后续更新
            </h2>
            <p className="text-white/70 mb-6">
              订阅51每日大赛，第一时间获取最新吃瓜爆料
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 font-label font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer"
            >
              返回首页看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}